import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useTheme } from "../components/ThemeProvider";
import { getBookingPalette } from "../components/booking/BookingTheme";
import {
  BOOKING_FONT_FAMILY,
  EmptyState,
  RetryButton,
  SearchInput,
  StepCard,
} from "../components/booking/BookingPrimitives";
import {
  fetchOrganizationDetail,
  pickAddress,
  pickTranslation,
  resolveStorageUrl,
} from "../helper/bookingApi";

function parseUrlToken(token) {
  if (!token) return null;
  const [idPart, ...nameParts] = String(token).split(",");
  const id = Number(idPart);
  if (!idPart || Number.isNaN(id)) return null;
  const rawName = nameParts.join(",");
  let name = rawName || "";
  try {
    name = decodeURIComponent(rawName);
  } catch (_err) {
    name = rawName;
  }
  return { id, name: name.replace(/-/g, " ").trim() };
}

function makeUrlToken(id, label) {
  if (id == null) return "";
  const safeLabel = String(label || id)
    .trim()
    .replace(/[\\/]+/g, "-")
    .replace(/\s+/g, "-");
  return `${id},${encodeURIComponent(safeLabel || id)}`;
}

function getBranches(detail) {
  if (Array.isArray(detail?.branches)) return detail.branches;
  if (Array.isArray(detail?.branch)) return detail.branch;
  if (Array.isArray(detail)) return detail;
  return [];
}

function getOrganization(detail, fallback) {
  if (detail?.organization) return detail.organization;
  if (detail?.organisation) return detail.organisation;
  if (detail && !Array.isArray(detail)) return detail;
  return fallback;
}

function getImagePath(node) {
  return (
    node?.logo_path ||
    node?.logo ||
    node?.image_path ||
    node?.organizationImage ||
    node?.image ||
    ""
  );
}

function getInitials(name) {
  return String(name || "N")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function buildMapQuery({ orgName, branchName, address }) {
  return [branchName || orgName, address].filter(Boolean).join(", ");
}

function buildMapEmbedUrl(query) {
  return query
    ? `https://maps.google.com/maps?q=${encodeURIComponent(query)}&output=embed`
    : "";
}

const INITIAL_BRANCH_VISIBLE_COUNT = 12;
const BRANCH_VISIBLE_INCREMENT = 12;

function BranchCard({ palette, branch, orgName, orgToken, lang, bookingBasePath }) {
  const { t } = useTranslation();
  const branchName = pickTranslation(branch, lang);
  const address = pickAddress(branch) || t("booking.branch.addressFallback");
  const bookingHref = `${bookingBasePath}/${orgToken}/${makeUrlToken(branch.id, branchName)}?step=service`;

  return (
    <article
      className="public-org-branch-card"
      style={{
        background: palette.surfaceAlt,
        border: palette.border,
        borderRadius: 18,
        padding: "clamp(14px, 3vw, 18px)",
        display: "grid",
        gap: 14,
      }}
    >
      <div className="d-flex align-items-start gap-3">
        <span
          aria-hidden="true"
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: palette.accentSoft,
            border: `1px solid ${palette.accentBorder}`,
            color: palette.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.4rem",
            flexShrink: 0,
          }}
        >
          <i className="las la-map-marker-alt" />
        </span>
        <div style={{ minWidth: 0 }}>
          <h3
            className="fw-700 mb-1"
            style={{
              color: palette.text,
              fontSize: "1.04rem",
              lineHeight: 1.25,
            }}
          >
            {branchName || orgName}
          </h3>
          <p
            className="mb-0"
            style={{
              color: palette.textMuted,
              fontSize: "0.9rem",
              lineHeight: 1.45,
            }}
          >
            {address}
          </p>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <Link
          to={bookingHref}
              className="btn btn-outline-main pill fw-500"
              style={{padding: "10px 13px"}}
        >
          {t("booking.publicOrg.bookBranch")}
        </Link>
      </div>
    </article>
  );
}

const OrganizationPublicPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { bookingOrg } = useParams();
  const token = useMemo(() => parseUrlToken(bookingOrg), [bookingOrg]);
  const { isDarkMode } = useTheme();
  const palette = useMemo(() => getBookingPalette(!isDarkMode), [isDarkMode]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [branchSearch, setBranchSearch] = useState("");
  const [visibleBranchCount, setVisibleBranchCount] = useState(INITIAL_BRANCH_VISIBLE_COUNT);

  const bookingBasePath = lang === "en" ? "/en/online-booking" : "/online-booking";

  const load = useCallback(async () => {
    if (!token?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOrganizationDetail(lang, token.id);
      setDetail(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [lang, token?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const fallbackOrg = useMemo(
    () => (token ? { id: token.id, name: token.name, urlName: token.name } : null),
    [token],
  );
  const organization = getOrganization(detail, fallbackOrg) || fallbackOrg || {};
  const branches = useMemo(() => getBranches(detail), [detail]);
  const normalizedBranchSearch = branchSearch.trim().toLowerCase();
  const filteredBranches = useMemo(() => {
    if (!normalizedBranchSearch) return branches;
    return branches.filter((branch) => {
      const branchName = pickTranslation(branch, lang).toLowerCase();
      const branchAddress = pickAddress(branch).toLowerCase();
      return (
        branchName.includes(normalizedBranchSearch) ||
        branchAddress.includes(normalizedBranchSearch)
      );
    });
  }, [branches, lang, normalizedBranchSearch]);
  const visibleBranches = filteredBranches.slice(0, visibleBranchCount);
  const hasBranchTools = branches.length > 3;
  const remainingBranchCount = Math.max(filteredBranches.length - visibleBranches.length, 0);
  const orgName = pickTranslation(organization, lang) || token?.name || t("booking.publicOrg.fallbackName");
  const orgAddress = pickAddress(organization);
  const logo = resolveStorageUrl(getImagePath(organization));
  const orgToken = makeUrlToken(token?.id || organization?.id, orgName);
  const firstBranch = filteredBranches[0] || branches[0];
  const mapQuery = buildMapQuery({
    orgName,
    branchName: firstBranch ? pickTranslation(firstBranch, lang) : "",
    address: firstBranch ? pickAddress(firstBranch) : orgAddress,
  });
  const mapUrl = buildMapEmbedUrl(mapQuery);

  useEffect(() => {
    setVisibleBranchCount(INITIAL_BRANCH_VISIBLE_COUNT);
  }, [branchSearch, branches.length]);

  return (
    <>
      <section
        className="public-org-page"
        style={{
          fontFamily: BOOKING_FONT_FAMILY,
          background: palette.pageBg,
          color: palette.text,
          paddingBottom: "clamp(60px, 10vw, 120px)",
        }}
      >
        <div
          style={{
            background: "var(--main-gradient)",
            padding: "clamp(42px, 8vw, 82px) 0 clamp(80px, 12vw, 130px)",
          }}
        >
          <div className="container container-two">
            <div className="public-org-hero">
              <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                <span
                  style={{
                    width: "clamp(64px, 12vw, 92px)",
                    height: "clamp(64px, 12vw, 92px)",
                    borderRadius: 22,
                    background: "rgba(255,255,255,0.96)",
                    border: "1px solid rgba(255,255,255,0.9)",
                    color: "hsl(166 100% 34%)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "clamp(1.5rem, 4vw, 2.4rem)",
                    fontWeight: 800,
                    overflow: "hidden",
                    flexShrink: 0,
                    boxShadow: "0 18px 42px rgba(15,23,42,0.16)",
                  }}
                >
                  {logo ? (
                    <img
                      src={logo}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    getInitials(orgName)
                  )}
                </span>
                <div style={{ minWidth: 0 }}>
                  {/* <p
                    className="mb-2 fw-700"
                    style={{
                      color: "rgba(255,255,255,0.82)",
                      fontSize: "0.9rem",
                    }}
                  >
                    {t("booking.publicOrg.eyebrow")}
                  </p> */}
                  <h1
                    className="fw-800 mb-2"
                    style={{
                      color: "#fff",
                      fontSize: "clamp(1.75rem, 4.8vw, 3.1rem)",
                      lineHeight: 1.08,
                      wordBreak: "break-word",
                    }}
                  >
                    {orgName}
                  </h1>
                  <p
                    className="mb-0"
                    style={{
                      color: "rgba(255,255,255,0.86)",
                      fontSize: "clamp(0.95rem, 1.8vw, 1.05rem)",
                      lineHeight: 1.55,
                      maxWidth: 720,
                    }}
                  >
                    {t("booking.publicOrg.subtitle")}
                  </p>
                </div>
              </div>
              <Link
                to={bookingBasePath}
                className="pill fw-700"
                style={{
                  background: "#fff",
                  color: "#0f172a",
                  border: "none",
                  padding: "10px 20px",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                }}
              >
                {t("booking.publicOrg.allOrganizations")}
              </Link>
            </div>
          </div>
        </div>

        <div className="container container-two" style={{ marginTop: -72 }}>
          <div className="public-org-layout">
            <StepCard palette={palette}>
              <div style={{ padding: "clamp(18px, 4vw, 24px)" }}>
                {loading ? (
                  <div
                    style={{
                      minHeight: 280,
                      borderRadius: 18,
                      background: palette.surfaceAlt,
                      border: palette.border,
                    }}
                  />
                ) : error ? (
                  <EmptyState
                    palette={palette}
                    icon="las la-exclamation-circle"
                    title={t("booking.publicOrg.loadError")}
                    action={<RetryButton palette={palette} onClick={load} />}
                  />
                ) : (
                  <>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                      <div>
                        <h2
                          className="fw-800 mb-1"
                          style={{ color: palette.text, fontSize: "1.35rem" }}
                        >
                          {t("booking.publicOrg.branchesTitle")}
                        </h2>
                        <p
                          className="mb-0"
                          style={{ color: palette.textMuted, fontSize: "0.94rem" }}
                        >
                          {t("booking.publicOrg.branchesSubtitle")}
                        </p>
                      </div>
                      <span
                        className="pill fw-700"
                        style={{
                          background: palette.accentSoft,
                          border: `1px solid ${palette.accentBorder}`,
                          color: palette.accent,
                          padding: "8px 12px",
                        }}
                      >
                        <i className="las la-map-marker-alt me-1" aria-hidden="true" />
                        {t("booking.organization.branchCount", { count: branches.length })}
                      </span>
                    </div>

                    {branches.length ? (
                      <>
                        {hasBranchTools ? (
                          <div className="public-org-branch-tools">
                            <SearchInput
                              palette={palette}
                              value={branchSearch}
                              onChange={setBranchSearch}
                              placeholder={t("booking.publicOrg.searchBranch")}
                            />
                            <span
                              className="public-org-branch-count pill fw-700"
                              style={{
                                background: palette.surfaceAlt,
                                border: palette.border,
                                color: palette.textMuted,
                              }}
                            >
                              {t("booking.publicOrg.showingCount", {
                                shown: visibleBranches.length,
                                total: filteredBranches.length,
                              })}
                            </span>
                          </div>
                        ) : null}

                        {filteredBranches.length ? (
                          <>
                            <div
                              className={`public-org-branches${
                                branches.length <= 3 ? " public-org-branches--small" : ""
                              }`}
                            >
                              {visibleBranches.map((branch) => (
                                <BranchCard
                                  key={branch.id}
                                  palette={palette}
                                  branch={branch}
                                  orgName={orgName}
                                  orgToken={orgToken}
                                  lang={lang}
                                  bookingBasePath={bookingBasePath}
                                />
                              ))}
                            </div>
                            {remainingBranchCount ? (
                              <div className="public-org-show-more">
                                <button
                                  type="button"
                                  className="pill fw-700"
                                  onClick={() =>
                                    setVisibleBranchCount(
                                      (count) => count + BRANCH_VISIBLE_INCREMENT,
                                    )
                                  }
                                  style={{
                                    background: palette.accent,
                                    color: "#061415",
                                    border: "none",
                                    padding: "10px 18px",
                                  }}
                                >
                                  {t("booking.publicOrg.showMore", {
                                    count: Math.min(
                                      BRANCH_VISIBLE_INCREMENT,
                                      remainingBranchCount,
                                    ),
                                  })}
                                </button>
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <EmptyState
                            palette={palette}
                            icon="las la-search"
                            title={t("booking.publicOrg.noMatchingBranches")}
                          />
                        )}
                      </>
                    ) : (
                      <EmptyState
                        palette={palette}
                        icon="las la-map-marker"
                        title={t("booking.publicOrg.noBranches")}
                      />
                    )}
                  </>
                )}
              </div>
            </StepCard>

            <aside className="public-org-map-card">
              <StepCard palette={palette}>
                <div style={{ padding: "clamp(14px, 3vw, 18px)", display: "grid", gap: 14 }}>
                  <div>
                    <h2
                      className="fw-800 mb-1"
                      style={{ color: palette.text, fontSize: "1.18rem" }}
                    >
                      {t("booking.publicOrg.mapTitle")}
                    </h2>
                    <p
                      className="mb-0"
                      style={{ color: palette.textMuted, fontSize: "0.9rem", lineHeight: 1.45 }}
                    >
                      {mapQuery || t("booking.publicOrg.mapFallback")}
                    </p>
                  </div>
                  <div
                    style={{
                      minHeight: 260,
                      borderRadius: 18,
                      overflow: "hidden",
                      border: palette.border,
                      background: palette.surfaceAlt,
                    }}
                  >
                    {mapUrl ? (
                      <iframe
                        title={t("booking.publicOrg.mapTitle")}
                        src={mapUrl}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        style={{ width: "100%", height: "100%", border: 0, display: "block" }}
                      />
                    ) : (
                      <div
                        className="h-100 d-grid text-center"
                        style={{
                          placeItems: "center",
                          color: palette.textMuted,
                          padding: 24,
                        }}
                      >
                        <div>
                          <i
                            className="las la-map-marked-alt d-block mb-2"
                            style={{ fontSize: 44, color: palette.accent }}
                            aria-hidden="true"
                          />
                          {t("booking.publicOrg.mapFallback")}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </StepCard>
            </aside>
          </div>
        </div>
      </section>

      <style>{`
        .public-org-page,
        .public-org-page * {
          font-family: ${BOOKING_FONT_FAMILY} !important;
          letter-spacing: 0 !important;
          font-synthesis-weight: none;
        }

        .public-org-page h1,
        .public-org-page h2,
        .public-org-page h3,
        .public-org-page h4,
        .public-org-page .fw-800,
        .public-org-page .fw-700,
        .public-org-page .fw-600,
        .public-org-page .fw-500,
        .public-org-page button,
        .public-org-page a,
        .public-org-page .pill {
          font-family: ${BOOKING_FONT_FAMILY} !important;
          font-weight: 700 !important;
          letter-spacing: 0 !important;
        }

        .public-org-page p,
        .public-org-page input,
        .public-org-page textarea {
          font-family: ${BOOKING_FONT_FAMILY} !important;
          font-weight: 400 !important;
          letter-spacing: 0 !important;
        }

        .public-org-hero {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 24px;
        }

        .public-org-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(320px, 0.42fr);
          gap: 18px;
          align-items: start;
        }

        .public-org-map-card {
          position: sticky;
          top: 96px;
        }

        .public-org-branch-tools {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 10px;
          align-items: center;
          margin-bottom: 14px;
        }

        .public-org-branch-count {
          padding: 10px 13px;
          white-space: nowrap;
          font-size: 0.84rem;
        }

        .public-org-branches {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }

        .public-org-branches--small {
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        }

        .public-org-show-more {
          display: flex;
          justify-content: center;
          margin-top: 14px;
        }

        @media (max-width: 991px) {
          .public-org-layout {
            grid-template-columns: 1fr;
          }

          .public-org-map-card {
            position: static;
          }
        }

        @media (max-width: 767px) {
          .public-org-hero {
            flex-direction: column;
            align-items: flex-start;
          }

          .public-org-branch-tools {
            grid-template-columns: 1fr;
          }

          .public-org-branch-count {
            justify-self: flex-start;
          }

          .public-org-branches {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
};

export default OrganizationPublicPage;
